import { useLocation, useNavigate } from 'react-router-dom'
import { Pages } from '../../Pages'
import Button from '../../components/Button'
import ProfilePic from '../common/person/ProfilePic'
import { formatPersonName } from '../../utils/personUtils'
import { CsPersonSearchResultDto } from '../../api/person/models/searchCs/SearchPersonsCsDtos'
import { useCallback, useState } from 'react'
import { useAuth } from '../../auth/AuthContext'
import { completeLinkedInSignup } from './helpers/completeLinkedInSignup'

type ConfirmState = {
    linkedInUrl?: string
    matchedPerson?: CsPersonSearchResultDto
}

export default function SignUpLinkedInConfirm(): React.JSX.Element {
    const navigate = useNavigate()
    const location = useLocation()
    const { refresh } = useAuth()

    const state = (location.state as ConfirmState | null) ?? {}
    const match = state.matchedPerson
    const matchDisplayName = match ? formatPersonName(match) : null
    const [saving, setSaving] = useState(false)
    const [status, setStatus] = useState<string | null>(null)

    const handleBack = useCallback(() => {
        const target = state.linkedInUrl
            ? `${Pages.SignupLinkedIn}?linkedinUrl=${encodeURIComponent(
                  state.linkedInUrl,
              )}`
            : Pages.SignupLinkedIn
        navigate(target, { replace: true })
    }, [navigate, state.linkedInUrl])

    const handleContinue = useCallback(async () => {
        if (!state.linkedInUrl) {
            setStatus('LinkedIn URL missing. Please go back and try again.')
            return
        }

        try {
            setSaving(true)
            setStatus(null)

            const response = await completeLinkedInSignup({
                linkedinUrl: state.linkedInUrl,
                person: match,
            })

            if (response.status === 'needs_work_history_selection') {
                if (!match) {
                    setStatus('Please choose your LinkedIn profile again.')
                    return
                }

                navigate(Pages.SignupLinkedIn, {
                    replace: true,
                    state: {
                        resumeStage: 'select-job',
                        linkedInUrl: state.linkedInUrl,
                        matchedPerson: match,
                        jobOptions: response.workHistoryOptions,
                    },
                })
                return
            }

            await refresh()
            navigate(Pages.Roles, { replace: true })
        } catch (err: unknown) {
            const message =
                err instanceof Error
                    ? err.message
                    : 'Unable to complete signup. Please try again.'
            setStatus(message)
        } finally {
            setSaving(false)
        }
    }, [match, navigate, refresh, state.linkedInUrl])

    return (
        <div className="min-h-screen flex items-center justify-center bg-gray-50 p-6">
            <div className="bg-white shadow-lg rounded-lg p-10 text-center space-y-6 max-w-md w-full">
                <div className="space-y-3">
                    <img
                        src="/Contxt_Logo_Full_BLK_CROPPED.png"
                        className="w-40 mx-auto mb-8"
                        alt="Contxt"
                    />
                    <h2 className="text-2xl font-bold">
                        {matchDisplayName
                            ? 'Profile matched!'
                            : 'No profile matched'}
                    </h2>
                </div>
                {!match && (
                    <div className="space-y-4">
                        <p className="text-gray-600">
                            We couldn&apos;t find a matching LinkedIn profile.
                            Please adjust the URL to continue.
                        </p>
                        <button
                            type="button"
                            className="text-sm text-blue-600 hover:underline"
                            onClick={handleBack}
                            disabled={saving}
                        >
                            Change LinkedIn URL
                        </button>
                    </div>
                )}
                {match && (
                    <div>
                        <div className="flex flex-col items-center gap-3 text-center">
                            <ProfilePic
                                src={match?.linkedInProfilePicUrl}
                                size={96}
                                alt={
                                    matchDisplayName ||
                                    'LinkedIn profile picture'
                                }
                            />
                            <div className="space-y-1">
                                <p className="text-lg font-semibold text-gray-900">
                                    {matchDisplayName ||
                                        'LinkedIn profile saved'}
                                </p>
                                {match?.headline && (
                                    <p className="text-sm text-gray-600">
                                        {match.headline}
                                    </p>
                                )}
                            </div>
                            <Button
                                style="primary"
                                type="button"
                                classes="w-full"
                                onClick={handleContinue}
                                enabled={!saving}
                            >
                                Continue
                            </Button>
                            <button
                                type="button"
                                className="text-sm text-blue-600 hover:underline"
                                onClick={handleBack}
                                disabled={saving}
                            >
                                That's not me
                            </button>
                        </div>
                    </div>
                )}
                {status && (
                    <p className="text-sm text-red-600 whitespace-pre-wrap">
                        {status}
                    </p>
                )}
            </div>
        </div>
    )
}
